import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Wind, Activity, FileText, CheckCircle, Send, ArrowRight } from 'lucide-react';

const ModelingTechnicalSupport = () => {
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', company: '', phone: '', email: '',
    facilityType: '', modelingNeeds: '', projectScope: '', dataAvailability: '', 
    specificRequirements: '', additionalInfo: ''
  });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/services/modeling-technical`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        alert('Thank you! Your modeling and technical support request has been submitted successfully. We will contact you shortly.');
        setFormData({
          firstName: '', lastName: '', company: '', phone: '', email: '',
          facilityType: '', modelingNeeds: '', projectScope: '', dataAvailability: '', 
          specificRequirements: '', additionalInfo: ''
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your request. Please try again or contact us directly.');
    }
  };

  const serviceDeliverables = [
    {
      title: 'AERMOD Dispersion Modeling',
      description: 'Air dispersion modeling using Interior Alaska and Arctic meteorological data for permit applications.',
      icon: Wind
    },
    {
      title: 'Stack Testing Coordination',
      description: 'On-site consulting and coordination with certified testing vendors for source emission testing.',
      icon: Activity
    },
    {
      title: 'Fugitive Dust & Opacity',
      description: 'Fugitive dust control plans and opacity monitoring support for compliance certification.',
      icon: BarChart3
    },
    {
      title: 'NEPA Assessments',
      description: 'Environmental assessments and technical analysis for federal NEPA projects.',
      icon: FileText
    }
  ];

  const facilityTypes = [
    { value: '', label: 'Select facility type' },
    { value: 'power-plant', label: 'Power Plant' },
    { value: 'oil-gas', label: 'Oil & Gas Facility' },
    { value: 'mining', label: 'Mining Operation' },
    { value: 'industrial', label: 'Industrial Facility' },
    { value: 'federal-project', label: 'Federal Project' },
    { value: 'other', label: 'Other' }
  ];

  const modelingNeeds = [
    { value: '', label: 'Select modeling needs' },
    { value: 'aermod', label: 'AERMOD Dispersion Modeling' },
    { value: 'stack-testing', label: 'Stack Testing Coordination' },
    { value: 'fugitive-dust', label: 'Fugitive Dust Analysis' },
    { value: 'nepa', label: 'NEPA Environmental Assessment' },
    { value: 'meteorological', label: 'Meteorological Data Analysis' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="min-h-screen" data-testid="modeling-technical-page">
      {/* Hero Section */}
      <section
        className="relative bg-gradient-to-r from-blue-900 to-blue-700 text-white py-20"
        style={{
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(https://images.unsplash.com/photo-1451187580459-43490279c0fa?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Modeling & Technical Support
            </h1>
            <p className="text-xl text-gray-100 mb-8">
              Advanced technical expertise for complex environmental assessments. AERMOD dispersion modeling, stack testing coordination, and NEPA analysis using Alaska-specific meteorological data.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#contact-form"
                className="inline-flex items-center px-6 py-3 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-colors shadow-lg"
              >
                Request Technical Analysis
                <Send className="ml-2" size={20} />
              </a>
              <a
                href="#key-deliverables"
                className="inline-flex items-center px-6 py-3 bg-white text-blue-700 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg"
              >
                View Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Advanced Technical Expertise</h2>
            <p className="text-lg text-gray-600 mb-4 leading-relaxed">
              Complex environmental assessments require specialized technical expertise and accurate modeling. Our technical support services provide the advanced analysis needed for permit applications, compliance certification, and environmental impact assessments.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              We specialize in Alaska-specific challenges including Arctic meteorological conditions, Interior Alaska dispersion patterns, and federal project requirements for remote locations.
            </p>
          </div>
        </div>
      </section>

      {/* Key Deliverables - Amber Jewel Icons */}
      <section id="key-deliverables" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Technical Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized modeling and technical analysis for Alaska operations
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {serviceDeliverables.map((deliverable, index) => {
              const IconComponent = deliverable.icon;
              return (
                <div key={index} className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-xl shadow-md hover:shadow-xl transition-shadow">
                  {/* Jewel Style Icon - Amber */}
                  <div className="bg-amber-500 w-16 h-16 rounded-lg flex items-center justify-center shadow-lg mb-4">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{deliverable.title}</h3>
                  <p className="text-gray-600">{deliverable.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Comprehensive Technical Services</h2>
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Dispersion Modeling (AERMOD)</h3>
              <p className="text-gray-600 mb-4">
                Air dispersion modeling using AERMOD with Interior Alaska and Arctic meteorological data. We provide modeling oversight for PSD permits, Title V applications, and regulatory compliance demonstrations.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  Alaska-specific meteorological data processing
                </li>
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  Complex terrain analysis for mountainous regions
                </li>
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  NAAQS and PSD increment analysis
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Stack Testing Coordination</h3>
              <p className="text-gray-600 mb-4">
                On-site consulting and coordination with certified EPA Method testing vendors. We ensure proper test protocols, data validation, and compliance certification.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  Pre-test protocol development and agency approval
                </li>
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  On-site oversight during testing events
                </li>
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  Data validation and final report review
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Fugitive Dust & Opacity Monitoring</h3>
              <p className="text-gray-600 mb-4">
                Fugitive dust control plans and opacity monitoring support for unpaved roads, material handling, and construction activities.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  Dust control plan development for remote sites
                </li>
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  Opacity monitoring and EPA Method 9 certification
                </li>
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  Cold-weather dust management strategies
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3">NEPA Environmental Assessments</h3>
              <p className="text-gray-600 mb-4">
                Technical analysis and documentation for federal NEPA projects including Environmental Assessments (EA) and Environmental Impact Statements (EIS).
              </p>
              <ul className="space-y-2">
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  Air quality impact analysis for federal actions
                </li>
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  Coordination with federal land management agencies
                </li>
                <li className="flex items-start text-sm text-gray-700">
                  <CheckCircle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                  Public comment response preparation
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-amber-600 text-white p-8 rounded-t-xl">
              <div className="flex items-center mb-4">
                <Send className="w-10 h-10 mr-4" />
                <div>
                  <h2 className="text-3xl font-bold">Request Technical Analysis</h2>
                  <p className="text-amber-100 mt-2">Tell us about your modeling and technical support needs</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 rounded-b-xl shadow-xl border border-gray-200">
              {/* Contact Information */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information *</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Company *</label>
                  <input type="text" name="company" value={formData.company} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                  </div>
                </div>
              </div>

              {/* Project Details */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Project Details</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Facility Type</label>
                  <select name="facilityType" value={formData.facilityType} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent">
                    {facilityTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Modeling Needs</label>
                  <select name="modelingNeeds" value={formData.modelingNeeds} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent">
                    {modelingNeeds.map(need => (
                      <option key={need.value} value={need.value}>{need.label}</option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Scope</label>
                  <textarea name="projectScope" value={formData.projectScope} onChange={handleChange} rows="3" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="Briefly describe your project scope and objectives"></textarea>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Availability</label>
                  <textarea name="dataAvailability" value={formData.dataAvailability} onChange={handleChange} rows="2" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="What data do you have available? (meteorological, emissions, etc.)"></textarea>
                </div>
              </div>

              {/* Specific Requirements */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Specific Requirements or Concerns *</label>
                <textarea name="specificRequirements" value={formData.specificRequirements} onChange={handleChange} required rows="4" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="Please describe your specific technical requirements, concerns, or questions"></textarea>
              </div>

              {/* Additional Information */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Information</label>
                <textarea name="additionalInfo" value={formData.additionalInfo} onChange={handleChange} rows="3" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent" placeholder="Any additional information you'd like to share"></textarea>
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-center">
                <button type="submit" className="inline-flex items-center px-8 py-4 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors shadow-lg">
                  Submit Technical Analysis Request
                  <Send className="ml-2" size={20} />
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Alaska-Specific Technical Expertise
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            AERMOD modeling, stack testing coordination, and NEPA analysis for remote operations
          </p>
          <Link to="/contact" className="inline-flex items-center px-8 py-4 bg-amber-500 text-white rounded-lg font-semibold hover:bg-amber-600 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105">
            Get Expert Technical Support
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ModelingTechnicalSupport;
